// components/tracking/TrackingMap.jsx
import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

const TrackingMap = ({
  vehicles,
  selectedVehicle,
  hoveredVehicle,
  setHoveredVehicle,
  setSelectedVehicle,
  showTrafficLayer,
  setShowTrafficLayer,
  mapInitialized,
}) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const trafficLayerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const initializingRef = useRef(false);

  // Initialize map once
  useEffect(() => {
    if (initializingRef.current || mapInitialized.current) return;

    initializingRef.current = true;

    const initMap = () => {
      if (!window.google) {
        initializingRef.current = false;
        return;
      }

      // Wait for the DOM element
      const mapElement = document.getElementById("google-map");
      if (!mapElement) {
        initializingRef.current = false;
        setTimeout(initMap, 100);
        return;
      }

      try {
        const map = new window.google.maps.Map(mapElement, {
          zoom: 6,
          center: { lat: 20.5937, lng: 78.9629 },
          mapTypeId: "roadmap",
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ],
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
        });

        mapInstanceRef.current = map;
        mapInitialized.current = true;

        // Initialize traffic layer
        trafficLayerRef.current = new window.google.maps.TrafficLayer();
        if (showTrafficLayer) {
          trafficLayerRef.current.setMap(map);
        }

        // Wait for map to be fully loaded
        window.google.maps.event.addListenerOnce(map, "idle", () => {
          setIsMapReady(true);
        });
      } catch (error) {
        console.error("Error initializing map:", error);
        initializingRef.current = false;
      }
    };

    if (!window.google) {
      const script = document.createElement("script");
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyARm0zb4v57rZQ0XI9QfAb93MDm-wrgZw8&libraries=geometry";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(initMap, 100);
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps script");
        initializingRef.current = false;
      };
      document.head.appendChild(script);
    } else {
      setTimeout(initMap, 100);
    }
  }, []);

  // Update traffic layer
  useEffect(() => {
    if (trafficLayerRef.current && mapInstanceRef.current && isMapReady) {
      trafficLayerRef.current.setMap(
        showTrafficLayer ? mapInstanceRef.current : null
      );
    }
  }, [showTrafficLayer, isMapReady]);

  // Update markers when vehicles change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google || !isMapReady) return;

    // Clear existing markers - FIXED: Access the marker property from the stored objects
    markersRef.current.forEach((markerObj) => {
      if (markerObj.marker) {
        window.google.maps.event.clearInstanceListeners(markerObj.marker);
        markerObj.marker.setMap(null);
      }
    });
    markersRef.current = [];

    // Check if we have valid vehicles
    if (!vehicles || vehicles.length === 0) {
      mapInstanceRef.current.setCenter({ lat: 20.5937, lng: 78.9629 });
      mapInstanceRef.current.setZoom(6);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let validVehicleCount = 0;

    vehicles.forEach((vehicle) => {
      // Validate position data
      if (
        !vehicle.position ||
        typeof vehicle.position.lat !== "number" ||
        typeof vehicle.position.lng !== "number" ||
        isNaN(vehicle.position.lat) ||
        isNaN(vehicle.position.lng)
      ) {
        return;
      }

      const position = {
        lat: vehicle.position.lat,
        lng: vehicle.position.lng,
      };

      const marker = new window.google.maps.Marker({
        position: position,
        map: mapInstanceRef.current,
        title: `${vehicle.number} - ${vehicle.driver}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor:
            vehicle.status === "active"
              ? "#10B981"
              : vehicle.status === "inactive"
                ? "#EF4444"
                : vehicle.status === "loading"
                  ? "#F59E0B"
                  : "#6B7280",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        optimized: true,
      });

      let hoverTimeout = null;

      marker.addListener("click", () => {
        setSelectedVehicle(vehicle);
      });

      marker.addListener("mouseover", () => {
        // Delay hover to prevent flickering
        hoverTimeout = setTimeout(() => {
          setHoveredVehicle(vehicle);
          marker.setIcon({
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 13,
            fillColor:
              vehicle.status === "active"
                ? "#10B981"
                : vehicle.status === "inactive"
                  ? "#EF4444"
                  : vehicle.status === "loading"
                    ? "#F59E0B"
                    : "#6B7280",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          });
        }, 100);
      });

      marker.addListener("mouseout", () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
        }
        setHoveredVehicle(null);
        marker.setIcon({
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor:
            vehicle.status === "active"
              ? "#10B981"
              : vehicle.status === "inactive"
                ? "#EF4444"
                : vehicle.status === "loading"
                  ? "#F59E0B"
                  : "#6B7280",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        });
      });

      // Store both marker and vehicle for reference
      markersRef.current.push({ marker, vehicle });

      try {
        bounds.extend(position);
        validVehicleCount++;
      } catch (error) {
        console.warn("Error extending bounds for vehicle:", vehicle.number);
      }
    });

    // Fit bounds if we have valid vehicles
    if (validVehicleCount > 0) {
      try {
        mapInstanceRef.current.fitBounds(bounds, {
          padding: { top: 80, right: 80, bottom: 80, left: 80 },
        });

        if (validVehicleCount === 1) {
          const listener = window.google.maps.event.addListenerOnce(
            mapInstanceRef.current,
            "bounds_changed",
            () => {
              mapInstanceRef.current.setZoom(
                Math.min(14, mapInstanceRef.current.getZoom())
              );
            }
          );
        }
      } catch (error) {
        console.error("Error fitting bounds:", error);
        mapInstanceRef.current.setCenter({ lat: 20.5937, lng: 78.9629 });
        mapInstanceRef.current.setZoom(6);
      }
    }
  }, [vehicles, isMapReady]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Live Map</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {vehicles.filter((v) => v.status === "active").length} vehicles
              active
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowTrafficLayer(!showTrafficLayer)}
              className={`px-4 py-2 rounded-xl font-medium flex items-center space-x-2 transition-all duration-300 ${
                showTrafficLayer
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {showTrafficLayer ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
              <span className="text-sm">Traffic</span>
            </button>
          </div>
        </div>
      </div>
      <div className="relative h-[600px] bg-gray-100">
        <div id="google-map" className="w-full h-full" />
        {!isMapReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading map...</p>
            </div>
          </div>
        )}
        {isMapReady && vehicles.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 pointer-events-none">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <Eye className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">
                No vehicles to display
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Vehicles will appear here when available
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingMap;
